import React, { useContext, useMemo } from 'react';
import { updateLastActivity } from '../../Utils/LastActivity';
import type { UserContextProps } from '../../Contexts/UserContext';
import UserContext from '../../Contexts/UserContext';
import type { LocalStorageContextProps } from '../../Contexts/LocalStorageContext';
import LocalStorageContext from '../../Contexts/LocalStorageContext';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import type { JSX } from 'react';
import type Folder from '../../Models/FolderModel';
import type MemoTemplate from '../../Models/MemoTemplateModel';
import { datetimeToVisualString } from '../../Utils/DateTimeParser';
import type { SystemContextProps } from '../../Contexts/SystemContext';
import SystemContext from '../../Contexts/SystemContext';

type FolderInfoDialogProps = {
    controller: boolean,
    onClose: () => void,
    folder: Folder,
    memoTemplates: MemoTemplate[],
};

function FolderInfoDialog(props: Readonly<FolderInfoDialogProps>): JSX.Element {

    const userContext: UserContextProps = useContext<UserContextProps>(UserContext);
    const localStorageContext: LocalStorageContextProps = useContext<LocalStorageContextProps>(LocalStorageContext);
    const systemContext: SystemContextProps = useContext<SystemContextProps>(SystemContext);

    const memoTemplate: MemoTemplate | null = useMemo<MemoTemplate | null>(() => props.memoTemplates.find((currentMemoTemplate: MemoTemplate) => currentMemoTemplate.templateId === props.folder.templateId) ?? null, [props.folder.templateId, props.memoTemplates]);

    function handleClose(): void {

        updateLastActivity(userContext, localStorageContext);

        props.onClose();
    }

    return (
        <Dialog 
            open={props.controller} 
            onClose={handleClose}
            PaperProps={{
                sx: {
                    position: 'fixed',
                },
            }}
        >
            <DialogTitle>Folder Info</DialogTitle>
            <DialogContent>
                <table>
                    <tbody>
                        <tr>
                            <td style={{
                                textAlign: 'right',
                            }}>
                                Name:
                            </td>
                            <td style={{
                                padding: '10px 0px 10px 10px',
                            }}>
                                {props.folder.name}
                            </td>
                        </tr>
                        <tr>
                            <td style={{
                                textAlign: 'right',
                            }}>
                                Created:
                            </td>
                            <td style={{
                                padding: '10px 0px 10px 10px',
                            }}>
                                {datetimeToVisualString(props.folder.createdDate, false, systemContext.serverTimezoneOffsetMinutes ?? 0)}
                            </td>
                        </tr>
                        <tr>
                            <td style={{
                                textAlign: 'right',
                            }}>
                                Template:
                            </td>
                            <td style={{
                                padding: '10px 0px 10px 10px',
                            }}>
                                {memoTemplate?.name ?? '<None>'}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={handleClose}
                    color='error'
                    variant='contained'
                    style={{
                        width: '90px',
                        height: '32px',
                        lineHeight: 'normal',
                        marginRight: '10px',
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default FolderInfoDialog;